// Pre-auth region resolution.
//
// A request's region decides which database serves it — the `location: us|eu`
// header stamped on every axios call (see hooks/useProgramContext). That header
// is derived from `localStorage.orgLocation` (written at login from the Auth0
// org) or the token claim, and until now fell back to a hardcoded 'us'.
//
// Before anyone logs in there is no user, no token and no org, so the PoP that
// answered this request is the best signal we have: if a US edge served the app,
// treat the visitor as a US visitor. That is all this module supplies — the
// FALLBACK. It never overwrites a region we already know from the org or token.
//
// The backend owns the strategy (GET /v4/region/config):
//   directoryEnabled=false -> SINGLE-REGION deployment (one stack, several
//     databases): nothing to resolve, so this module stays out of the way.
//   directoryEnabled=true  -> MULTI-REGION: the PoP's region is the default, and
//     a known address can be resolved against the global directory.
//
// Deliberately uses bare fetch(), NOT the app's axios: the global request
// interceptor stamps `authorization: Bearer undefined` on anonymous calls, and
// its 401 teardown can clear localStorage — neither is wanted before login.

export type Region = 'us' | 'eu';

const REGION_KEY = 'dosen.region.default';
const DIRECTORY_KEY = 'dosen.region.directory';
const REGION_COOKIE = 'region';
// Bounded so a slow or dead lookup can never hold up first paint for long.
const RESOLVE_TIMEOUT_MS = 1500;
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

interface RegionConfigResponse {
  region: string;
  directoryEnabled: boolean;
  cookieEnabled: boolean;
}

export const isRegion = (value: string): value is Region => value === 'us' || value === 'eu';

// Lowercase at the boundary: the legacy backend's getConnectionString compares
// against exactly "us" and falls through to EU for anything else, so an
// upper-case value would silently select the wrong database.
const normalize = (value: string | null | undefined): Region | null => {
  const v = (value || '').trim().toLowerCase();
  return isRegion(v) ? v : null;
};

// Same base the rest of the V4 client uses: '' in local dev (relative -> the
// /v4 dev proxy), an absolute host in deployed environments.
const v4Base = (): string => process.env.REACT_APP_API_V4 || '';

/** The PoP-derived default, or null when unknown / single-region. */
export const getDefaultRegion = (): Region | null => {
  try {
    return normalize(localStorage.getItem(REGION_KEY));
  } catch {
    return null;
  }
};

/** Whether this deployment routes by the global directory at all. */
export const isDirectoryEnabled = (): boolean => {
  try {
    return localStorage.getItem(DIRECTORY_KEY) === 'true';
  } catch {
    return false;
  }
};

const setCookie = (region: Region): void => {
  const secure = window.location.protocol === 'https:' ? '; secure' : '';
  document.cookie = `${REGION_COOKIE}=${region}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
};

const clearCookie = (): void => {
  document.cookie = `${REGION_COOKIE}=; path=/; max-age=0; samesite=lax`;
};

async function getJson<T>(path: string): Promise<T | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), RESOLVE_TIMEOUT_MS);
  try {
    const res = await fetch(`${v4Base()}${path}`, {
      signal: controller.signal,
      headers: { accept: 'application/json' }
    });
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    // Never let region resolution break app start-up.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function refresh(): Promise<void> {
  const config = await getJson<RegionConfigResponse>('/v4/region/config');
  if (!config) return;

  try {
    localStorage.setItem(DIRECTORY_KEY, String(config.directoryEnabled));

    // Single-region deployment: no default to remember, no cookie to carry.
    if (!config.directoryEnabled) {
      localStorage.removeItem(REGION_KEY);
      clearCookie();
      return;
    }

    const region = normalize(config.region);
    if (!region) return;

    localStorage.setItem(REGION_KEY, region);
    window.__REGION__ = region;
    // The cookie exists purely so the edge can route on it; the app itself
    // reads localStorage. Switchable independently of the directory.
    if (config.cookieEnabled) setCookie(region);
    else clearCookie();
  } catch {
    // localStorage unavailable (private mode) — the in-memory global still helps.
    window.__REGION__ = normalize(config.region) || undefined;
  }
}

/**
 * Resolve the region BEFORE the app chunk mounts. A cold start awaits one
 * bounded round trip; afterwards the cached value is used immediately and
 * revalidated in the background, so start-up is never blocked twice.
 */
export function bootstrapRegion(): Promise<void> {
  const cached = getDefaultRegion();
  if (cached) {
    window.__REGION__ = cached;
    void refresh();
    return Promise.resolve();
  }
  // On a RELOAD of a logged-in session the tokens are already in localStorage,
  // so the home probe can pin the region BEFORE anything mounts. Without this,
  // mount-time effects (feature flags, setup guide, lifecycle boards) race the
  // getToken() probe and fire inside the pre-probe window, where the header
  // chain still bottoms out at the hardcoded 'us' — a burst of wrong-region
  // 404s on every reload (seen live 2026-07-30). First-ever login has no token
  // here; that path is covered by the getToken() probe as before.
  return refresh().then(() => probeHomeRegion().then(() => undefined));
}

/**
 * Ask the directory where a specific address is homed. This is the piece an
 * identifier-first login screen calls once it knows the user's email: a hit
 * beats the PoP, which is what keeps a travelling user on their home region.
 * Returns null when the deployment is single-region or the lookup fails.
 */
export async function resolveRegionForEmail(email: string): Promise<Region | null> {
  if (!email || !isDirectoryEnabled()) return null;
  const result = await getJson<{ region: string }>(`/v4/region?email=${encodeURIComponent(email)}`);
  return normalize(result?.region);
}

// Post-login home-region PROBE.
//
// Users without an org location (platform admins, orgs with no metadata.location)
// used to fall through useProgramContext's chain to the hardcoded 'us', and the
// edge then routed every call to the US stack — whose database does not contain
// them: instant "User not provisioned" (seen live 2026-07-29). Until the global
// directory exists, the only source that truly knows a user's home is the
// database itself, so ask it: GET /users/me per region until one answers 200.
//
// requireUser makes the wrong-region signal safe and clean — it 404s BEFORE any
// handler runs, so probing the wrong region reads nothing and writes nothing.
//
// Region order is 'eu' FIRST, deliberately: today's edge rule for `location: us`
// is non-deterministic (route override flaps per config wave). If 'us' were
// probed first during a broken window, the request would fall through to the EU
// origin, find the user there and pin 'us' — poisoning the session. Probing
// 'eu' first means EU-homed users (every real staging user) resolve on attempt
// one and never depend on the flaky path.
const PROBE_TIMEOUT_MS = 6000; // consumption cold starts are slower than the 1.5 s config lookup

async function probeRegionOnce(region: Region, accessToken: string): Promise<'hit' | 'miss' | 'abort'> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${v4Base()}/v4/users/me`, {
      signal: controller.signal,
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${accessToken}`,
        location: region
      }
    });
    if (res.ok) return 'hit';
    if (res.status === 404) return 'miss'; // "User not provisioned" → not homed here
    return 'abort'; // 401/403/5xx: not a region signal — leave the chain untouched
  } catch {
    return 'abort'; // network/timeout: fail open, never worse than today
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Pin the caller's home region right after login, when nothing authoritative
 * (org location) answers for them. Writes the same key the pre-auth bootstrap
 * uses, so useProgramContext's getDefaultRegion() link picks it up for every
 * subsequent call. No-ops when the region is already known. Never throws.
 */
export async function probeHomeRegion(): Promise<Region | null> {
  try {
    // Org location wins the header chain anyway — probing would be redundant.
    if (normalize(localStorage.getItem('orgLocation'))) return null;
  } catch {
    /* localStorage unavailable — the probe below still helps via __REGION__ */
  }
  const cached = getDefaultRegion();
  if (cached) return cached;

  let accessToken = '';
  try {
    accessToken = JSON.parse(localStorage.getItem('tokens') || '{}').access_token || '';
  } catch {
    /* ignore */
  }
  if (!accessToken) return null;

  for (const region of ['eu', 'us'] as Region[]) {
    const outcome = await probeRegionOnce(region, accessToken);
    if (outcome === 'abort') return null;
    if (outcome === 'hit') {
      try {
        localStorage.setItem(REGION_KEY, region);
      } catch {
        /* private mode — in-memory global below still applies */
      }
      window.__REGION__ = region;
      return region;
    }
  }
  return null; // not provisioned anywhere: let the app surface its normal error
}
